import { Theme, css, useTheme } from '@emotion/react';
import { transparentize } from 'polished';
import { ArrowSquareOut, CreditCard, CurrencyCircleDollar, PencilSimple, TrendUp } from '@phosphor-icons/react';
import type { AccountAccountingSummary } from 'api/account/methods/loadAccountAccountingSummary';
import { getProductSettings } from 'common/accounting/AccountingDocuments';
import { Button } from 'client/components/Button';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Pipeline } from 'client/components/Postcard/Pipeline';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

const containerStyle = (theme: Theme) => css`
  border: 2px solid ${theme.color.primary[100]};
  border-radius: ${borderRadius}px;
`;

const headerStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 2px solid ${theme.color.primary[100]};
`;

const breakdownStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 24px;
  list-style: none;

  > p {
    display: flex;
    align-items: center;
    gap: 3px;
    margin-top: -4px;
    margin-bottom: 4px;

    > svg {
      transform: translateY(1px);
    }
  }

  > li > ul {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 0;
    padding: 0;
    list-style: none;

    > li {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    > hr {
      display: block;
      flex: 1;
      margin: 0;
      height: 0;
      border: none;
      border-bottom: 2px dotted ${theme.color.grey[60]};
      // background-color: ${theme.color.grey[60]};
    }
  }
`;

const estimateStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  border-top: 2px solid ${theme.color.primary[100]};
  overflow: hidden;

  > div {
    flex: 1;
    padding: 24px;

    > div {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      background-color: ${transparentize(0.5, theme.color.background)};

      box-shadow: ${transparentize(0, theme.color.background)} 0px 0px 32px;

      > span {
        transform: translateY(2px);
      }

      > p {
        transform: translateY(-4px);
      }
    }

    > p {
      display: flex;
      align-items: center;
      gap: 3px;

      > svg {
        transform: translateY(1px);
      }
    }
  }

  > hr {
    display: block;
    margin: 0;
    width: 2px;
    border: none;
    background-color: ${theme.color.primary[100]};
  }
`;

const pipelineStyle = css`
  position: absolute;
  width: 1024px;
  transform: translate(-240px, -140px) scaleX(-1);
  z-index: -1;
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const longCurrencyFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 3,
});

const shortCurrencyFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
});

const formatPrice = (value: number, long = true) =>
  value === 0 ? 'Free' : (long ? longCurrencyFormatter : shortCurrencyFormatter).format(value);

export const PaidPlanBreakdown = ({ accounting }: { accounting: AccountAccountingSummary }) => {
  const theme = useTheme();

  const items = [...accounting.currentMonthProducts].sort((a, b) =>
    getProductSettings(a.productID).sortOrder > getProductSettings(b.productID).sortOrder ? 1 : -1
  );

  const { currentMonthTotal, currentMonthEstimate, previousMonthTotal, freeCreditLeft } = accounting;
  const currentMonthActualPrice = Math.max(0, currentMonthTotal - freeCreditLeft);

  return (
    <>
      <section css={containerStyle}>
        <div css={headerStyle}>
          <ListItemFigure icon={<CreditCard />} />
          <ListItemText primary="Pay-as-you-go" secondary="Access to all features, billing based on usage" />
          <Button icon={<PencilSimple />} />
        </div>
        <ul css={breakdownStyle}>
          <Typography variant="body2">
            <CurrencyCircleDollar weight="bold" /> Current month cost breakdown
          </Typography>

          {items.map((p) => (
            <li>
              <Typography variant="body2" component="ul">
                <li>
                  <ListItemFigure
                    size={20}
                    image={{ src: 'https://hotshot.b-cdn.net/project/100003873.svg', alt: 'asd', shape: 'hexagon' }}
                  />
                  {getProductSettings(p.productID).name}
                </li>
                <hr />
                <li>{formatPrice(p.price, false)}</li>
              </Typography>
            </li>
          ))}
        </ul>
        <section css={estimateStyle}>
          <Pipeline color={theme.color.primary[60]} css={pipelineStyle} />
          <div>
            <div>
              <Typography variant="h5">{formatPrice(currentMonthActualPrice, false)}</Typography>
              <Typography variant="body2">/ month</Typography>
            </div>
            <Typography variant="body2">
              <CurrencyCircleDollar weight="bold" />
              Current month costs.{' '}
              {currentMonthActualPrice == 0 && currentMonthTotal > 0 ? `Costs ${formatPrice(currentMonthTotal, false)}` : ''}
            </Typography>
          </div>
          <hr />
          <div>
            <div>
              <Typography variant="h5">{currentMonthEstimate === null ? 'n/a' : formatPrice(currentMonthEstimate, false)}</Typography>
              <Typography variant="body2">/ month</Typography>
            </div>
            <Typography variant="body2">
              <TrendUp weight="bold" />
              Forecasted month end costs
            </Typography>
          </div>
          <hr />
          <div>
            <div>
              <Typography variant="h5">{formatPrice(previousMonthTotal, false)}</Typography>
              <Typography variant="body2">/ month</Typography>
            </div>
            <Typography variant="body2">
              <CurrencyCircleDollar weight="bold" />
              Previous month costs
            </Typography>
          </div>
        </section>
      </section>
      <div css={actionsStyle}>
        <Button icon={<ArrowSquareOut weight="bold" />}>Payment history</Button>
        <Button icon={<ArrowSquareOut weight="bold" />}>Manage payment details</Button>
      </div>
    </>
  );
};
